import { Box } from "@mui/material";
import ConfirmDialog from "components/ConfirmDialog";
import RedirectBackButton from "components/RedirectBackButton";
import { FORM_ERROR } from "final-form";
import useGlobalContext from "hooks/globalData";
import useBoolean from "hooks/useBoolean";
import useTrashablePromises from "hooks/useTrashablePromises";
import { useState } from "react";
import { useIntl } from "react-intl";
import { useQueryClient } from "react-query";
import {
  IOrganizationDetails,
  IOrganizationSaveRequest,
  OrganizationSaveRequest,
  SaveResponse,
} from "services/KnowfullyAdminClient";
import { OrganizationStatus } from "types/common-types";
import translateError from "utils/translateError";
import OrganizationForm from "./OrganizationForm";

type OrganizationUpdateFormProps = {
  organization: IOrganizationDetails;
  onOrganizationUpdated: () => void;
  onCancel: () => void;
};

const OrganizationUpdateForm = ({
  organization,
  onOrganizationUpdated,
  onCancel,
}: OrganizationUpdateFormProps) => {
  const { registerPromise } = useTrashablePromises();
  const { formatMessage: t } = useIntl();
  const { organizationsUrl, organizationService } = useGlobalContext();
  const [confirmDeactivate, confirmDeactivateActions] = useBoolean(false);
  const [
    pendingValues,
    setPendingValues,
  ] = useState<IOrganizationSaveRequest>();
  const queryClient = useQueryClient();

  const saveOrganization = async (values: IOrganizationSaveRequest) => {
    try {
      const response: SaveResponse = await registerPromise(
        // Application-instance linkage is managed outside OrganizationSaveRequest.
        organizationService.saveOrganization(
          new OrganizationSaveRequest({
            ...values,
            id: organization.id,
          })
        )
      );

      queryClient.invalidateQueries("organizations");
      queryClient.invalidateQueries("organizationService.getOrganizationTree");
      queryClient.invalidateQueries(["organization", organization.id]);
      onOrganizationUpdated();
      return response;
    } catch (err) {
      return { [FORM_ERROR]: translateError(err, t) };
    }
  };

  const onSubmit = async (values: IOrganizationSaveRequest) => {
    if (values.organizationStatusId === OrganizationStatus.Deactivated) {
      setPendingValues(values);
      confirmDeactivateActions.setTrue();
      return;
    }
    return saveOrganization(values);
  };

  const handleConfirmDeactivate = async () => {
    if (pendingValues) {
      const result = await saveOrganization(pendingValues);
      confirmDeactivateActions.setFalse();
      return result;
    }
  };

  return (
    <>
      <Box my={2}>
        <RedirectBackButton
          redirectRoute={organizationsUrl()}
          labelKey="backToListButtonLabel"
        />
      </Box>
      <OrganizationForm
        organization={organization}
        onSubmit={onSubmit}
        submitLabel={t({ id: "organizationFormUpdateButton" })}
        onCancel={onCancel}
      />
      <ConfirmDialog
        dialogTitleKey="deactivateOrganizationTitle"
        contentKey="deactivateOrganizationQuestion"
        confirmLabel="continueButton"
        open={confirmDeactivate}
        onCancel={confirmDeactivateActions.setFalse}
        handleConfirm={handleConfirmDeactivate}
      />
    </>
  );
};

export default OrganizationUpdateForm;
